import React, { useEffect, useMemo, useState } from 'react';
import Table from 'rsuite/Table';

import {
  PaidUnredeemedOrders,
  SuppliersBalance,
  UsersBalance,
  usePaidUnredeemedOrders,
  useSuppliersBalance,
  useUsersBalance,
} from '../../../api/finances';
import { toMoney } from '../../../utils/common.utils';
import classNames from 'classnames';
import { useLocalStorage } from '@mantine/hooks';
import moment from 'moment/moment';
import { User, UserMultiSelect } from '../../common/user-multi-select';
import { formatDateValue } from '../../../utils/date-value';
import { DatePicker } from 'rsuite';
import { Controller, useForm } from 'react-hook-form';
import { InputWrapper } from '../../common/input-wrapper';

type Form = {
  asOfDate: Date | null;
};

export const FinanceDashboard = () => {
  const [isFocused, setIsFocused] = useState(false);

  const { control, watch } = useForm<Form>({
    defaultValues: {
      asOfDate: new Date(),
    },
  });

  const asOfDate = watch('asOfDate');

  const [excludeUsers, setExcludeUsers] = useLocalStorage<Array<User>>({
    key: 'exclude-users',
    defaultValue: [],
  });

  const [suppliersDate, setSuppliersDate] = useLocalStorage<Date>({
    key: 'suppliers-balance-date-update',
    defaultValue: undefined,
  });
  const [usersDate, setUsersDate] = useLocalStorage<Date>({
    key: 'users-balance-date-update',
    defaultValue: undefined,
  });

  const formattedDate = asOfDate ? formatDateValue(asOfDate) : 'today';

  const [cacheSuppliersData, setCacheSuppliersData] =
    useLocalStorage<SuppliersBalance>({
      key: `cache-suppliers-balance-${formattedDate}`,
      defaultValue: undefined,
    });
  const [cacheUsersData, setCacheUsersData] = useLocalStorage<UsersBalance>({
    key: `cache-users-balance-${excludeUsers.join('-')}-${formattedDate}`,
    defaultValue: undefined,
  });
  const [cacheUnredeemedData, setCacheUnredeemedData] =
    useLocalStorage<PaidUnredeemedOrders>({
      key: `cache-unredeemed-orders-${formattedDate}`,
      defaultValue: undefined,
    });

  const excludeIds = useMemo(
    () =>
      excludeUsers
        ?.filter((user) => user?.internalId)
        .map((user) => user.internalId!),
    [excludeUsers],
  );

  const { isLoading: suppliersIsLoading, mutate: mutateSuppliers } =
    useSuppliersBalance((data) => {
      setSuppliersDate(new Date());
      setCacheSuppliersData(data);
    }, asOfDate);

  const { isLoading: usersIsLoading, mutate: mutateUsers } = useUsersBalance(
    (data) => {
      setUsersDate(new Date());
      setCacheUsersData(data);
    },
    excludeIds,
    asOfDate,
  );

  const { isLoading: unredeemedIsLoading, mutate: mutateUnredeemed } =
    usePaidUnredeemedOrders((data) => {
      setCacheUnredeemedData(data);
    }, asOfDate);

  useEffect(() => {
    mutateUsers();
  }, [excludeUsers]);

  useEffect(() => {
    mutateSuppliers();
    mutateUsers();
    mutateUnredeemed();
  }, [asOfDate]);

  return (
    <div className={'w-100'}>
      <div className={'mb-3'}>
        <Controller
          name="asOfDate"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <InputWrapper
                title="Показать баланс на дату"
                error={fieldState.error?.message}
                className="w-25"
              >
                <DatePicker
                  className={'w-100'}
                  format={'dd.MM.yyyy'}
                  isoWeek={true}
                  shouldDisableDate={(date) => {
                    // Disable future dates
                    return date > new Date();
                  }}
                  onChange={(v) => {
                    field.onChange(v);
                  }}
                  value={field.value}
                />
              </InputWrapper>
            );
          }}
        />
      </div>
      <div className={'d-flex gap-2 w-100'}>
        <div className={'w-50'}>
          <h3>Баланс экспедиторов</h3>
          <Table
            data={cacheSuppliersData || []}
            headerHeight={57}
            autoHeight
            loading={!cacheSuppliersData && suppliersIsLoading}
          >
            <Table.Column width={200}>
              <Table.HeaderCell>Экспедитор</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return rowData.name;
                }}
              </Table.Cell>
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Баланс выкупов</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return toMoney(
                    rowData.purchaseCurrency === 'cny'
                      ? rowData.balancePurchase.cny
                      : rowData.balancePurchase.usd,
                    rowData.purchaseCurrency,
                  );
                }}
              </Table.Cell>
            </Table.Column>
            <Table.Column width={90}>
              <Table.HeaderCell>Валюта</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return rowData.purchaseCurrency === 'cny' ? '¥' : '$';
                }}
              </Table.Cell>
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Баланс доставок</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return toMoney(
                    rowData.deliveryCurrency === 'cny'
                      ? rowData.balanceDelivery.cny
                      : rowData.balanceDelivery.usd,
                    rowData.deliveryCurrency,
                  );
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column width={90}>
              <Table.HeaderCell>Валюта</Table.HeaderCell>
              <Table.Cell>
                {(rowData) => {
                  return rowData.deliveryCurrency === 'cny' ? '¥' : '$';
                }}
              </Table.Cell>
            </Table.Column>
          </Table>
          <div className={'d-flex align-items-center gap-2 mt-1'}>
            <button
              type="button"
              onClick={() => {
                mutateSuppliers();
              }}
              className="btn btn-success ms-2"
            >
              {suppliersIsLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Обновить'
              )}
            </button>
            <div>
              {' '}
              Последнее обновление:{' '}
              {moment(suppliersDate).format('DD/MM в HH:mm')}
            </div>
          </div>
        </div>

        <div className={'w-50'}>
          <h3>Баланс клиентов</h3>
          <Table
            data={[
              cacheUsersData?.total || {},
              cacheUsersData?.positive || {},
              cacheUsersData?.negative || {},
            ]}
            headerHeight={57}
            autoHeight
            loading={!cacheUsersData && usersIsLoading}
          >
            <Table.Column fullText width={150}>
              <Table.HeaderCell>{''}</Table.HeaderCell>
              <Table.Cell>
                {(_, rowIndex) => {
                  return rowIndex === 0
                    ? 'Общий'
                    : rowIndex === 1
                      ? 'Положительный'
                      : 'Отрицательный';
                }}
              </Table.Cell>
            </Table.Column>
            <Table.Column fullText flexGrow={1}>
              <Table.HeaderCell>Юань, ¥</Table.HeaderCell>
              <Table.Cell className={'border-start border-light-subtle'}>
                {(rowData, rowIndex) => {
                  return (
                    <div
                      className={classNames(
                        rowIndex == 1 && 'color-green',
                        rowIndex == 2 && 'color-red',
                      )}
                    >
                      {toMoney(rowData?.cny || 0, 'cny')}
                    </div>
                  );
                }}
              </Table.Cell>
            </Table.Column>
            <Table.Column fullText flexGrow={1}>
              <Table.HeaderCell>Доллар, $</Table.HeaderCell>
              <Table.Cell className={'border-start border-light-subtle'}>
                {(rowData, rowIndex) => {
                  return (
                    <div
                      className={classNames(
                        rowIndex == 1 && 'color-green',
                        rowIndex == 2 && 'color-red',
                      )}
                    >
                      {toMoney(rowData?.usd || 0, 'usd')}
                    </div>
                  );
                }}
              </Table.Cell>
            </Table.Column>

            <Table.Column fullText flexGrow={1}>
              <Table.HeaderCell>Рубли, ₽</Table.HeaderCell>
              <Table.Cell className={'border-start border-light-subtle'}>
                {(rowData, rowIndex) => {
                  return (
                    <div
                      className={classNames(
                        rowIndex == 1 && 'color-green',
                        rowIndex == 2 && 'color-red',
                      )}
                    >
                      {toMoney(rowData?.bonus || 0, 'rub')}
                    </div>
                  );
                }}
              </Table.Cell>
            </Table.Column>
          </Table>

          <div className={'w-50 mt-3 ms-2'}>
            <UserMultiSelect
              placeholder="Исключить пользователей"
              name={'ExcludeIds'}
              value={excludeUsers}
              onChange={(users) => {
                setExcludeUsers(users);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              onFocus={() => {
                setIsFocused(true);
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  maxHeight: isFocused ? 'none' : 30,
                  overflowY: 'hidden',
                }),
              }}
              roles={['client']}
            />
          </div>
          <div className={'d-flex align-items-center gap-2 mt-1'}>
            <button
              type="button"
              onClick={() => {
                mutateUsers();
                mutateUnredeemed();
              }}
              className="btn btn-success  ms-2"
            >
              {usersIsLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                'Обновить'
              )}
            </button>
            <div>
              {' '}
              Последнее обновление: {moment(usersDate).format('DD/MM в HH:mm')}
            </div>
          </div>
          <div className={'ms-2'}>
            Долг по доставкам (клиенты):{' '}
            {usersIsLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              `${+cacheUsersData?.positive.usd - +cacheUsersData?.deliveryDebt > 0 ? '+' : ''}${toMoney(+cacheUsersData?.positive.usd - +cacheUsersData?.deliveryDebt)} $`
            )}
          </div>
          <div className={'ms-2'}>
            Оплаченные, не выкупленные заказы:{' '}
            {unredeemedIsLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              `${cacheUnredeemedData?.ordersCount || 0} шт. | ${toMoney(cacheUnredeemedData?.totalPrice || '0', 'cny')}`
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
